import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { IconButton } from "../../../core/components/buttons/IconButton";
import { DropdownSelect } from "../../../core/components/dropdown/DropdownSelect";
import { getLanguage } from "../../../core/store/appSlice";
import { useMainStore } from "../../../core/store/mainStore";
import {
  IPAD_MINI_BREAKPOINT,
  IPAD_MINI_BREAKPOINT_NUMBER,
  MOBILE_BREAKPOINT,
} from "../../../core/styles/breakpoints";
import { useStyles } from "../../../core/styles/theme";
import { hexWithOpacity } from "../../../core/utils/hexWithOpacity";
import useWindowDimensions from "../../../core/utils/useWindowDimensions";
import { useHeaderContext } from "../context/useHeaderContext";
import { LogoComponent } from "./LogoComponent";

export const HEADER_HEIGHT = 70;

export const Header = () => {
  const language = useMainStore(getLanguage);
  const setLanguage = useMainStore((state) => state.setLanguage);
  const { customBackHandler, hideBackButton } = useHeaderContext();
  const navigate = useNavigate();
  const width = useWindowDimensions().width;
  const isDesktop = width >= IPAD_MINI_BREAKPOINT_NUMBER;
  const isAppointmentFlow = window.location.pathname.includes("appointment");

  const languageOptions = [
    { value: "de", label: isDesktop ? "Deutsch 🇩🇪" : "DE" },
    { value: "tr", label: isDesktop ? "Türkçe 🇹🇷" : "TR" },
    { value: "en", label: isDesktop ? "English 🏴󠁧󠁢󠁥󠁮󠁧󠁿" : "EN" },
  ];

  const theme = useStyles();

  const selectedLanguageOption =
    languageOptions.find((option) => option.value === language)?.label ||
    languageOptions[0].label;

  const handleGoBack = () => {
    if (customBackHandler) {
      customBackHandler();
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      <Container
        $noSideBar={!isAppointmentFlow || !isDesktop}
        $isDesktop={isDesktop}
      >
        <InnerContainer>
          <RightContainer>
            {!hideBackButton && (
              <IconButton
                iconName={"ArrowLeft"}
                primaryColor={theme.colors.medium}
                onClick={handleGoBack}
              />
            )}
          </RightContainer>
          {(!isDesktop || !isAppointmentFlow) && (
            <LogoContainer>
              <LogoComponent />
            </LogoContainer>
          )}
          <RightContainer>
            <DropdownSelect
              value={selectedLanguageOption}
              options={languageOptions}
              onChange={(_label, value) => {
                setLanguage(value as "de" | "en" | "tr");
              }}
              withoutError
              hideDropdownIcon
            />
          </RightContainer>
        </InnerContainer>
      </Container>
    </>
  );
};

const Container = styled.div<{ $noSideBar?: boolean; $isDesktop?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  z-index: 10;
  border-bottom: 1.5px solid;
  border-bottom-color: ${({ theme }) => hexWithOpacity(theme.colors.dark, 0.1)};
  background-color: ${({ theme, $noSideBar }) =>
    $noSideBar ? theme.colors.white : "transparent"};
  @media (min-width: ${IPAD_MINI_BREAKPOINT}) {
    border-bottom: ${({ $noSideBar }) => ($noSideBar ? "1.5px solid" : "none")};
    border-bottom-color: ${({ theme }) =>
      hexWithOpacity(theme.colors.dark, 0.1)};
    padding-left: ${({ $noSideBar }) => ($noSideBar ? "0" : "30vw")};
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 20px 10px 40px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 10px 20px 10px 20px;
  }
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const LogoContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;
